import { formatDate } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { EMPTY, catchError, concatMap, delay, exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';

import { DetailType } from '@ninety/platform/models/_shared/detail-type.enum';
import { User } from '@ninety/platform/models/_shared/user';
import { ItemType } from '@ninety/platform/models/enums/item-type';
import { LinkedItemTypeEnum } from '@ninety/platform/models/linked-items/linked-item-type-enum';
import { TodoDeleOptions } from '@ninety/platform/models/todos/todo-delete-options';
import { TodoMessageType } from '@ninety/platform/models/todos/todo-message-types';
import { TodoRealTimeMessage } from '@ninety/platform/models/todos/todo-realtime-message';
import { TodoRepeatType } from '@ninety/platform/models/todos/todo-repeat-types';
import { TodoSortFieldEnum } from '@ninety/platform/models/todos/todo-sort-field';
import {
  ExcelExportType,
  FilterService,
  FilterServiceActions,
  LocalStorageService,
  extractValueFromStore,
} from '@ninety/platform/services';
import {
  DetailViewActions,
  LayoutsTodosEvents,
  MazSharedActions,
  MazToolName,
  appActions,
  TeamListStateActions,
  TeamSelectors,
  UserListStateActions,
  selectIsMyNinetyUrl,
  FilterUserSelectors,
  UsersStateActions,
  selectCurrentUser,
  selectUserSettings,
  selectLanguage,
} from '@ninety/platform/state';
import { NotificationActions } from '@ninety/platform/state/app-global/notifications/notification.actions';
import { ConfirmDialogComponent, ConfirmDialogData, WarningConfirmDialogComponent } from '@ninety/ui/core';
import { FileService } from '@ninety/ui/legacy/core/services/file.service';
import { LibraryService } from '@ninety/ui/legacy/core/services/library.service';
import { NotifyService, NotifyV2Params } from '@ninety/ui/legacy/core/services/notify.service';
import { PrintApi, PrintService } from '@ninety/ui/legacy/core/services/print.service';
import { CreateDialogService } from '@ninety/web/pages/layouts/services/create-dialog.service';
import { MeetingsStateSelectors } from '@ninety/web/pages/meeting/_state/meetings.selectors';
import { MilestoneService } from '@ninety/web/pages/rocks/_shared/milestone.service';
import { IntegrationService } from '@ninety/web/pages/settings/user/integration/_api/integration.service';

import { TodoService } from '../../_shared/todo.service';
import { TeamTodosChildStateKey, TodoRootStateKey, selectTeamTodoState } from '../../_state';
import { RepeatTodoDeleteDialogComponent } from '../../repeat-todo-delete/repeat-todo-delete-dialog.component';
import { TodoApiService } from '../../services/todo-api.service';
import { createOrdinalUpdate } from '../_shared/todo-state.shared.effects';
import { TodoDetailActions } from '../detail/todo-detail.actions';
import { TodoDetailSelectors } from '../detail/todo-detail.selectors';
import { PersonalTodoActions } from '../personal/personal-todo.actions';

import {
  TeamTodoActions,
  TeamTodoInlineActions,
  TeamTodoTrashActions,
  TodoAgreementActions,
} from './team-todo.actions';
import { TeamTodoFacade } from './team-todo.facade';
import { TeamTodoSelectors } from './team-todo.selectors';

function getDefaultActionParams(): NotifyV2Params {
  return { actionText: 'X', config: { duration: 4_000, panelClass: 'plain-text-action' } };
}

@Injectable()
export class TeamTodoEffects {
  private readonly actions$ = inject(Actions);
  private readonly todoService = inject(TodoService);
  private readonly milestoneService = inject(MilestoneService);
  private readonly store = inject(Store);
  private readonly createDialogService = inject(CreateDialogService);
  private readonly integrationService = inject(IntegrationService);
  private readonly notifyService = inject(NotifyService);
  private readonly dialog = inject(MatDialog);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly todoApi = inject(TodoApiService);
  private readonly printService = inject(PrintService);
  private readonly libraryService = inject(LibraryService);
  private readonly facade = inject(TeamTodoFacade);
  private readonly fileService = inject(FileService);
  private readonly filterService = inject(FilterService);

  sortBy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.sortBy),
        concatLatestFrom(() => [this.store.select(TeamTodoSelectors.selectIsTodosUrl)]),
        filter(([, isTodosUrl]) => isTodosUrl),
        tap(([{ sort }, _]) => {
          if (sort.direction) {
            //direction is null when sort cycle is complete
            this.localStorageService.set(`${TodoRootStateKey}.${TeamTodosChildStateKey}.sort`, sort);
          } else {
            this.localStorageService.delete(`${TodoRootStateKey}.${TeamTodosChildStateKey}.sort`);
          }
        })
      ),
    { dispatch: false }
  );

  handleTeamChangeWhenInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamListStateActions.changeFilterBarTeam),
      concatLatestFrom(() => this.store.select(TeamTodoSelectors.selectTeamComponentInitialized)),
      filter(([, teamComponentInitialized]) => teamComponentInitialized),
      map(([{ id }]) => TeamTodoActions.teamSelected({ teamId: id }))
    )
  );

  initializeTeamComponent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.initializeTeamComponent),
      concatLatestFrom(({ onMeetingConclude }) =>
        onMeetingConclude
          ? this.store.select(MeetingsStateSelectors.selectCurrentMeetingTeamId)
          : this.store.select(TeamSelectors.selectFilterBarTeamId)
      ),
      switchMap(([, teamId]) => {
        return of(TeamTodoActions.setSelectedTeamId({ teamId }), TeamTodoActions.getTeam());
      })
    )
  );

  handleUserFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListStateActions.changeFilterBarUsers),
      concatLatestFrom(() => [this.store.select(TeamTodoSelectors.selectIsTodosUrl)]),
      filter(([, isTodosUrl]) => isTodosUrl),
      map(() => TeamTodoActions.getTeamInBackground())
    )
  );

  refreshTeamTodosOnMazToolUse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MazSharedActions.toolUse),
      filter(({ toolName }) => toolName === MazToolName.createTodo || toolName === MazToolName.editTodo),
      concatLatestFrom(() => this.store.select(TeamTodoSelectors.selectIsTeamTodosUrl)),
      filter(([, isTeamTodosUrl]) => isTeamTodosUrl),
      delay(3000),
      map(() => TeamTodoActions.getTeamInBackground())
    )
  );

  /** The main entry point for retrieving the current list of todos. Fires on any paging, sorting or archive change */
  getTeamTodos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TeamTodoActions.getTeam,
        TeamTodoActions.showArchived,
        TeamTodoActions.teamSelected,
        TeamTodoActions.paginationChange,
        TeamTodoActions.search,
        TeamTodoActions.deleteSeriesSuccess,
        TeamTodoActions.toggleUserPendingAgreements,
        TeamTodoActions.getTeamInBackground
      ),
      concatLatestFrom(() => [this.store.select(TeamTodoSelectors.selectTodoQueryParamsWithUsers)]),
      switchMap(([, params]) => {
        return this.todoService.getTodos(params).pipe(
          map(response => TeamTodoActions.getTeamSuccess({ response: response })),
          catchError((error: unknown) => of(TeamTodoActions.getTeamFailure({ error })))
        );
      })
    )
  );

  getTeamTodosOnSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.sortBy),
      concatLatestFrom(() => [this.store.select(TeamTodoSelectors.selectTodoQueryParamsWithUsers)]),
      switchMap(([, params]) => {
        return this.todoService.getTodos(params).pipe(
          map(response => TeamTodoActions.getTeamSuccess({ response: response })),
          catchError((error: unknown) => of(TeamTodoActions.getTeamFailure({ error })))
        );
      })
    )
  );

  //Calls the todo service to update ordinals when a user moves a todo item in the list
  //Ordinals are used as the default sort order when no sort has been set
  updateOrdinals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.updateOrdinals),
      concatLatestFrom(() => [
        this.store.select(selectTeamTodoState),
        this.store.select(TeamTodoSelectors.selectTodoQueryParams),
      ]),
      filter(([, state, _]) => state.todos.ids.length && !state.showArchived),
      switchMap(([action, state, params]) => {
        const todosWithChanges = createOrdinalUpdate(action, state);

        return this.todoService
          .updateOrdinals(
            todosWithChanges,
            state.ordinalKey,
            0,
            state.teamId,
            state.sortField,
            state.sortDirection,
            false,
            params.pageSize
          )
          .pipe(
            map(_ => TeamTodoActions.updateOrdinalsSuccess()),
            catchError(() => of(TeamTodoActions.updateOrdinalsFailure()))
          );
      }),
      // Never called as the request observable isn't being tracked
      catchError(() => of(TeamTodoActions.updateOrdinalsFailure()))
    )
  );

  updateOrdinalsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.updateOrdinalsSuccess),
      map(_ => TeamTodoActions.clearSort())
    )
  );

  clearSort$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.clearSort),
        tap(_ => this.localStorageService.delete(`${TodoRootStateKey}.${TeamTodosChildStateKey}.sort`))
      ),
    { dispatch: false }
  );

  updateTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.update, TeamTodoActions.updateInline, TeamTodoActions.updateDueDate),
      concatLatestFrom(() => [
        this.store.select(TeamTodoSelectors.selectSelectedTodoId),
        this.store.select(TodoDetailSelectors.selectedTodo),
      ]),
      concatMap(([action, selectedTodoId, detailSelectedTodo]) => {
        //use the selected _todo Id if not provided. Used by the detail service, which only provides the update values
        //TODO: Remove the todoDetailSelector._id once my90 and team todos page are no longer using the detail store.
        const todoId = action.id ?? selectedTodoId ?? detailSelectedTodo._id;

        //Pass whether the _todo is being marked/unmarked complete so that any associated
        //milestones can be updated in the success effect below.
        const completeStatusChange = action.todo.hasOwnProperty('completed');

        /** Update inline success triggers an additional effect to update detail inputs */
        return this.todoService.updateTodo(todoId, action.todo).pipe(
          map(response => {
            if (action.type === TeamTodoActions.update.type) {
              if (action.todo.hasOwnProperty('repeat')) {
                const repeatMessage =
                  action.todo.repeat !== TodoRepeatType.DontRepeat
                    ? `Repeat ${action.todo.repeat} created`
                    : `Repeat ${detailSelectedTodo.repeat} disabled`;
                this.notifyService.notify(repeatMessage);
              }
              return TeamTodoActions.updateSuccess({ todo: response, completeStatusChange });
            } else if (action.type === TeamTodoActions.updateDueDate.type) {
              return TeamTodoActions.updateDueDateSuccess({ todo: response, completeStatusChange });
            } else {
              return TeamTodoActions.updateInlineSuccess({ todo: response, completeStatusChange });
            }
          }),
          catchError(() => of(TeamTodoActions.updateFailure()))
        );
      })
    )
  );

  /**
   * Agreement Based Todos. Runs a normal update but updates the todo in state in the response
   * because todo acceptance/negotiation status is all handled server side.
   */
  acceptTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TodoAgreementActions.accept),
      switchMap(({ todoId, update }) =>
        this.todoService.updateTodo(todoId, update).pipe(
          map(response => TodoAgreementActions.acceptSuccess({ todo: response })),
          catchError((error: unknown) => of(TodoAgreementActions.acceptFailure({ error })))
        )
      )
    )
  );

  addMany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.addMany),
      concatLatestFrom(() => [this.store.select(selectIsMyNinetyUrl)]),
      exhaustMap(([{ todo, userIds, createdFrom }, isMyNinetyUrl]) =>
        this.todoService.create(todo, userIds, createdFrom).pipe(
          map(response => TeamTodoActions.addManySuccess({ response, isMyNinetyUrl })),
          catchError(() => of(TeamTodoActions.addManyFailure()))
        )
      )
    )
  );

  uploadAttachments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.addManySuccess),
        filter(({ response }) => !!response.fileAttachments?.length),
        switchMap(({ response }) =>
          this.libraryService.uploadTodoFilesWithScan(response.todos, response.fileAttachments).pipe(
            tap(({ failures }) => {
              if (failures.length) {
                this.notifyService.notify(`Some attachments couldn't be uploaded: ${failures.join(', ')}`);
              }
            })
          )
        )
      ),
    { dispatch: false }
  );

  syncSomeMilestoneFieldsFromTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.addManySuccess),
      //NOTE: if we get here it should only be one todo in the array
      //Universal create has multiple user selection disable for cases
      //when the todo is created from a milestone
      filter(({ response }) => !!response.todos[0].milestoneId),
      concatMap(({ response }) =>
        this.milestoneService
          .update(response.todos[0].milestoneId, {
            toDoId: response.todos[0]._id,
            dueDate: response.todos[0].dueDate,
            description: response.todos[0].description,
          })
          .pipe(
            map(() => TeamTodoActions.updateConnectedMilestoneSuccess()),
            catchError(() => of(TeamTodoActions.updateConnectedMilestoneFailure()))
          )
      )
    )
  );

  /**
   * Handles any integration errors that might have occurred while creating to-dos. To-do creation isn't blocked by external errors
   * but those errors are returned in the response so they can be handled here.
   */
  todoIntegrationFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.addManySuccess, TeamTodoInlineActions.createOneSuccess),
      concatLatestFrom(() => [this.store.select(selectCurrentUser), this.store.select(selectUserSettings)]),
      filter(([{ response }, currentUser]) => !!response.integrationErrors?.find(e => e.userId === currentUser?._id)),
      map(([{ response }, currentUser, userSettings]) => {
        /** Todos can be created for multiple users at once - check if any errors came back for the current user */
        const e = response.integrationErrors.find(e => e.userId === currentUser?._id);
        const settingsAlerts = { ...userSettings.settingsAlerts, integration: e.error };
        const integrations = cloneDeep(userSettings.integrations);
        integrations.google.requiresAuthentication = true;

        return UsersStateActions.updateOne({
          _id: currentUser?._id,
          changes: {
            settings: { ...currentUser?.settings, ...{ settingsAlerts, integrations } },
          } as Partial<User>,
        });
      })
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.updateUser),
      concatMap(({ id, todo }) =>
        this.todoService.updateTodo(id, todo).pipe(
          map(todo => TeamTodoActions.updateUserSuccess({ todo })),
          catchError(() => of(TeamTodoActions.updateUserFailure()))
        )
      )
    )
  );

  setCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.setCompleted),

      map(action =>
        TeamTodoActions.updateInline({
          id: action.id,
          todo: {
            completed: action.todo.completed,
            completedDate: action.todo.completedDate,
          },
        })
      )
    )
  );

  updatePersonalTodosOnMyNinety$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.updateSuccess),
      concatLatestFrom(() => [this.facade.isMyNinetyUrl$]),
      filter(([_, isMyNinetyUrl]) => isMyNinetyUrl),
      map(([{ todo }]) => {
        if (todo.isPersonal) return PersonalTodoActions.addToPersonal({ todo });
        else return PersonalTodoActions.removeFromPersonal({ todo });
      })
    )
  );

  refreshTeamTodosInSeriesInStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.updateSuccess, TeamTodoActions.updateInlineSuccess, TeamTodoActions.updateUserSuccess),
      map(({ todo }) => {
        if (todo.seriesId && todo.repeat !== TodoRepeatType.DontRepeat) {
          return TeamTodoActions.updateDisplayedTodosInSeries({ todo });
        }
        return appActions.noop();
      })
    )
  );

  removeAcceptedTodoFromStoreIfPendingFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TodoAgreementActions.acceptSuccess),
      concatLatestFrom(() => [this.store.select(TeamTodoSelectors.selectUserPendingAgreementsFilter)]),
      filter(([_, filter]) => filter),
      map(([{ todo }]) => TeamTodoActions.removeAcceptedTodoFromState({ id: todo._id }))
    )
  );

  //Update any milestones associated with a _todo as a result of a successful update
  updateMilestones$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.updateSuccess),
        map(action => {
          if (action.completeStatusChange && action.todo?.milestoneId) {
            this.milestoneService.update(action.todo.milestoneId, { isDone: action.todo.completed });
          }
        })
      ),
    { dispatch: false }
  );

  toggleArchived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.toggleArchived),
      exhaustMap(action =>
        this.todoService.toggleArchive(cloneDeep(action.todo)).pipe(
          map(todo => TeamTodoActions.toggleArchivedSuccess({ todo, id: todo._id })),
          catchError(() => of(TeamTodoActions.toggleArchivedFailure()))
        )
      )
    )
  );

  deleteConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.deleteConfirm),
      concatLatestFrom(() => this.store.select(selectLanguage)),
      exhaustMap(([action, language]) => {
        if (action.todo.seriesId && action.todo.repeat !== TodoRepeatType.DontRepeat) {
          return of(TeamTodoActions.openDeleteTodoDialog({ todo: action.todo }));
        }

        return this.dialog
          .open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
            data: ConfirmDialogData.WarningDialog({
              title: `Delete ${language?.todo?.item ?? 'To-Do'}`,
              message: `Are you sure you want to delete this ${language?.todo?.item ?? 'To-Do'}?`,
              additionalMessage: `This will move the ${language?.todo?.item ?? 'To-Do'} to the trash. Items in the trash will be permanently deleted after 90 days.`,
            }),
          })
          .afterClosed()
          .pipe(
            map((confirmed: boolean) => (confirmed ? TeamTodoActions.delete({ todo: action.todo }) : appActions.noop()))
          );
      })
    )
  );

  openDeleteTodoDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.openDeleteTodoDialog),
      exhaustMap(({ todo }) =>
        this.dialog
          .open(RepeatTodoDeleteDialogComponent, {
            data: { todo },
          })
          .afterClosed()
      ),
      filter(option => !!option),
      map(({ option, todo }) => {
        if (option === TodoDeleOptions.item) return TeamTodoActions.delete({ todo: todo });
        else return TeamTodoActions.deleteSeries({ todo: todo });
      })
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.delete),
      concatMap(({ todo }) =>
        this.todoService.delete(todo).pipe(
          map(() => TeamTodoActions.deleteSuccess({ id: todo._id })),
          catchError(() => of(TeamTodoActions.deleteFailure()))
        )
      )
    )
  );

  deleteSeries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.deleteSeries),
      concatMap(action =>
        this.todoService.deleteSeries(action.todo).pipe(
          map(_ => TeamTodoActions.deleteSeriesSuccess({ seriesId: action.todo.seriesId, todo: action.todo })),
          catchError(() => of(TeamTodoActions.deleteSeriesFailure()))
        )
      )
    )
  );

  createOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoInlineActions.createOne),
      exhaustMap(({ todo }) => {
        const { _id: _, ...noIdTodo } = todo;

        const newTodo = Object.assign(cloneDeep(noIdTodo), { originalDueDate: todo.dueDate });

        return this.todoService.createOne(newTodo).pipe(
          map(response => TeamTodoInlineActions.createOneSuccess({ response })),
          catchError(() => of(TeamTodoInlineActions.createOneFailure()))
        );
      })
    )
  );

  /**
   * Handles opening the create dialog, but does not respond to it being closed.
   *
   * New todos created by the create dialog are handed by {@link MyNinetyPageEffects#addTeamTodosToStateAfterPersist$}
   * in My90 and the {@link TodosComponent} on the main Todos page.
   */
  openCreateDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.openCreateDialog),
        switchMap(() => this.createDialogService.open({ itemType: ItemType.todo }))
      ),
    { dispatch: false }
  );

  //TODO: check from milestone todo to personal for errors or missing fields

  /** Creates a google task from a todo */
  createTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.createTask),
      concatMap(action =>
        this.integrationService.createTask(action.id).pipe(
          map(task => TeamTodoActions.createTaskSuccess({ todoId: action.id, googleTaskId: task.id })),
          catchError(() => of(TeamTodoActions.createTaskFailure()))
        )
      )
    )
  );

  syncTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.syncTasks),
      concatLatestFrom(() => this.store.select(TeamTodoSelectors.selectTeamId)),
      exhaustMap(([, teamId]) =>
        this.integrationService.syncTasks(teamId).pipe(
          tap(() => {
            const language = extractValueFromStore(this.store, selectLanguage);
            this.notifyService.notify(`${language?.todo?.items} successfully resynced with Google Tasks`);
          }),
          map(() => TeamTodoActions.getTeam()),
          catchError(() => of(TeamTodoActions.syncTasksFailure()))
        )
      )
    )
  );

  moveToBottom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.moveToBottom),
      concatLatestFrom(() => this.store.select(TeamTodoSelectors.selectTeamTodoCount)),
      map(([{ index: previousIndex }, todosCount]) =>
        TeamTodoActions.updateOrdinals({ previousIndex, currentIndex: todosCount - 1 })
      )
    )
  );

  moveToTop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.moveToTop),
      map(({ index: previousIndex }) => TeamTodoActions.updateOrdinals({ previousIndex, currentIndex: 0 }))
    )
  );

  makeItAnIssue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.makeItAnIssue),
        switchMap(action =>
          this.createDialogService.open({
            item: action.todo,
            itemType: ItemType.issue,
            createdFrom: {
              id: action.todo._id,
              type: LinkedItemTypeEnum.todo,
            },
          })
        )
      ),
    {
      dispatch: false,
    }
  );

  createTodo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.createTodo),
        switchMap(action =>
          this.createDialogService.open({
            item: action.todo,
            itemType: ItemType.todo,
            createdFrom: {
              id: action.todo._id,
              type: LinkedItemTypeEnum.todo,
            },
          })
        )
      ),
    {
      dispatch: false,
    }
  );

  createRock$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.createRock),
        switchMap(action =>
          this.createDialogService.open({
            item: action.todo,
            itemType: ItemType.rock,
          })
        )
      ),
    {
      dispatch: false,
    }
  );

  createHeadline$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.createHeadline),
        switchMap(action =>
          this.createDialogService.open({
            item: action.todo,
            itemType: ItemType.headline,
            createdFrom: {
              id: action.todo._id,
              type: LinkedItemTypeEnum.todo,
            },
          })
        )
      ),
    {
      dispatch: false,
    }
  );

  savePageSizeOnLocalStore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.savePageSizeOnLocalStore),
        tap(({ pageSize }) => {
          this.localStorageService.set(`${TodoRootStateKey}.${TeamTodosChildStateKey}.pagination.size`, pageSize);
        })
      ),
    { dispatch: false }
  );

  promptOnRequestToArchiveAllCompletedUserTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.requestArchiveAllCompletedUserTeam),
      switchMap(() => this.todoService.confirmArchiveCompletedDialog()),
      filter(shouldContinue => !!shouldContinue),
      map(() => TeamTodoActions.archiveAllCompletedUserTeam())
    )
  );

  archiveAllCompletedUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.archiveAllCompletedUserTeam),
      switchMap(() => this.todoApi.archiveAllCompletedUserTeam()),
      map(resp => TeamTodoActions.archiveAllCompletedUserTeamSuccess(resp)),
      catchError((error: unknown) => of(TeamTodoActions.archiveAllCompletedUserTeamFailure({ error })))
    )
  );

  archiveAllCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.archiveAllCompleted),
      switchMap(() =>
        this.todoService.archiveAllCompleted().pipe(
          filter(archivedCount => !!archivedCount),
          tap(archivedCount => {
            const language = extractValueFromStore(this.store, selectLanguage);
            // null if confirm dialog is canceled
            this.notifyService.notify(
              `${archivedCount} ${archivedCount === 1 ? language?.todo?.item : language?.todo?.items} archived.`,
              3000
            );
          })
        )
      ),
      map(() => TeamTodoActions.archiveAllCompletedSuccess()),
      catchError((error: unknown) => of(TeamTodoActions.archiveAllCompletedFailure({ error })))
    )
  );

  refetchTeamTodos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.archiveAllCompletedUserTeamSuccess, TeamTodoActions.archiveAllCompletedSuccess),
      map(() => TeamTodoActions.getTeam())
    )
  );

  print$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.print),
      concatLatestFrom(() => [
        this.store.select(selectTeamTodoState),
        this.store.select(FilterUserSelectors.selectFilterBarUserIds),
      ]),
      switchMap(([{ printOptions }, state, filterBarUserIds]) => {
        const userIds =
          filterBarUserIds == null || filterBarUserIds.includes('all')
            ? []
            : filterBarUserIds.filter(userId => userId !== 'all');
        return this.printService
          .openPdf(PrintApi.todos, {
            teamId: state.teamId,
            showArchived: state.showArchived,
            sortField: TodoSortFieldEnum[state.sortField],
            sortDirection: state.sortDirection,
            searchText: state.searchText,
            printOptions,
            ...(userIds.length > 0 ? { userIds } : {}),
          })
          .pipe(
            map(() => TeamTodoActions.printSuccess()),
            catchError(() => of(TeamTodoActions.printFailure()))
          );
      })
    )
  );

  deleteCommentTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.deleteComment),
      concatLatestFrom(() => [this.store.select(TeamTodoSelectors.selectTeamTodos)]),
      concatMap(([action, selectedTodos]) => {
        const selectedTodo = selectedTodos.find(t => t._id === action.event._id);

        const comments = selectedTodo.comments.filter(
          c =>
            c.userId !== action.event.comment.userId ||
            new Date(c.createdDate).getTime() !== new Date(action.event.comment.createdDate).getTime()
        );

        return this.todoService.updateTodo(selectedTodo._id, { comments: comments }).pipe(
          map(response => TeamTodoActions.updateSuccess({ todo: response, completeStatusChange: false })),
          catchError(() => of(TeamTodoActions.updateFailure()))
        );
      })
    )
  );

  /** An attachment was updated locally - broadcast the event */
  attachmentUploaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.attachmentUploaded, TeamTodoActions.attachmentRemoved),
        switchMap(action => {
          const messageType =
            action.type === TeamTodoActions.attachmentUploaded.type
              ? TodoMessageType.attachmentUpload
              : TodoMessageType.attachmentRemove;
          const payload = {
            messageType,
            document: action.event.attachment.parentId,
          } as TodoRealTimeMessage;

          return this.todoService.broadcastMessage(payload);
        })
      ),
    {
      dispatch: false,
    }
  );

  /** An attachment was reordered locally - broadcast the event */
  attachmentReordered$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoActions.attachmentReordered),
        switchMap(({ event }) => {
          const payload = {
            messageType: TodoMessageType.attachmentUpload,
            document: event._id,
          } as TodoRealTimeMessage;

          return this.todoService.broadcastMessage(payload);
        })
      ),
    {
      dispatch: false,
    }
  );

  select$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.select),
      map(({ todo }) => TodoDetailActions.openInDetail({ todo }))
    )
  );

  setSelectedIdOnReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.getTeamSuccess),
      concatLatestFrom(() => [this.store.select(TeamTodoSelectors.selectTodoIdFromRoute)]),
      filter(([_, todoId]) => !!todoId),
      map(([_, todoId]) => TeamTodoActions.setSelectedId({ todoId }))
    )
  );

  clearSelectedOnDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoActions.deselect),
      map(() => DetailViewActions.closed({ itemType: DetailType.todo }))
    )
  );

  loadDeletedTodos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoTrashActions.loadDeletedTodos),
      switchMap(({ teamId, page, pageSize, userIds, searchText, sortField, sortDirection }) =>
        // The FE paginator is 0-based; the .NET trash API is 1-based (Page must be > 0, else 400).
        // Convert here at the single outbound boundary so the rest of the FE stays 0-based.
        this.todoApi
          .getDeletedTodos({ teamId, page: page + 1, pageSize, userIds, searchText, sortField, sortDirection })
          .pipe(
            map(response =>
              TeamTodoTrashActions.loadDeletedTodosSuccess({
                todos: response.items,
                totalCount: response.metadata.total,
              })
            ),
            catchError((error: unknown) => of(TeamTodoTrashActions.loadDeletedTodosFailure({ error })))
          )
      )
    )
  );

  restoreTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoTrashActions.restoreTodo),
      exhaustMap(({ todoId }) =>
        this.todoApi.restoreTodo(todoId).pipe(
          map(() => TeamTodoTrashActions.restoreTodoSuccess({ todoId })),
          catchError((error: unknown) => of(TeamTodoTrashActions.restoreTodoFailure({ error })))
        )
      )
    )
  );

  restoreTodoSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoTrashActions.restoreTodoSuccess),
      switchMap(() =>
        of(
          NotificationActions.notifyV2({
            message: 'To-Do and associated content have been restored.',
            params: getDefaultActionParams(),
          }),
          // Silently re-fetch the live list so the restored item reappears without a loading spinner.
          TeamTodoActions.getTeamInBackground(),
          DetailViewActions.close()
        )
      )
    )
  );

  openDeleteForeverTodoConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoTrashActions.openDeleteForeverTodoConfirmation),
      concatLatestFrom(() => this.store.select(selectLanguage)),
      exhaustMap(([{ todo }, language]) =>
        this.dialog
          .open<WarningConfirmDialogComponent, ConfirmDialogData>(WarningConfirmDialogComponent, {
            autoFocus: false,
            disableClose: true,
            width: '450px',
            maxWidth: 450,
            data: {
              title: `Are you sure you want to delete this ${language?.todo?.item ?? 'To-Do'}?`,
              message: `This will remove the ${language?.todo?.item ?? 'To-Do'} and all its associated content. This action cannot be undone.`,
              confirmButtonText: 'Delete Forever',
            },
          })
          .afterClosed()
          .pipe(
            filter(confirmed => !!confirmed),
            map(() => TeamTodoTrashActions.permanentDeleteTodo({ todoId: todo._id }))
          )
      )
    )
  );

  permanentDeleteTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoTrashActions.permanentDeleteTodo),
      exhaustMap(({ todoId }) =>
        this.todoApi.permanentDeleteTodo(todoId).pipe(
          map(() => TeamTodoTrashActions.permanentDeleteTodoSuccess({ todoId })),
          catchError((error: unknown) => of(TeamTodoTrashActions.permanentDeleteTodoFailure({ error })))
        )
      )
    )
  );

  permanentDeleteTodoSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoTrashActions.permanentDeleteTodoSuccess),
      switchMap(() =>
        of(
          NotificationActions.notifyV2({
            message: 'To-Do permanently deleted.',
            params: getDefaultActionParams(),
          }),
          DetailViewActions.close()
        )
      )
    )
  );

  clearSearchOnTrashToggle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LayoutsTodosEvents.showTrashItems, LayoutsTodosEvents.hideTrashItems),
        tap(() => this.filterService.setSearchText(''))
      ),
    { dispatch: false }
  );

  downloadExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.exportToExcel),
      filter(({ exportType }) => exportType === ExcelExportType.teamTodo),
      concatLatestFrom(() => [
        this.store.select(TeamTodoSelectors.selectTodoQueryParamsWithUsers),
        this.filterService.selectedTeam$,
        this.store.select(selectLanguage),
        this.store.select(TeamTodoSelectors.selectShowTrash),
      ]),
      filter(([, , , , showTrash]) => !showTrash),
      switchMap(([, params, team, language]) =>
        this.todoService
          .downloadExcel(params)
          .pipe(
            tap(response => {
              const currentDate = new Date();
              const fileName = `${language.todo.items}_${team.name}_${formatDate(
                currentDate,
                'MMddyyyy',
                'en-US'
              )}.xlsx`;
              this.fileService.downloadExcelFile(response, fileName);
            })
          )
          .pipe(
            map(() => TeamTodoActions.downloadExcelSuccess()),
            catchError(() => of(TeamTodoActions.downloadExcelFailure()))
          )
      )
    )
  );

  /**
   * DV-01: Opens a soft-deleted To-Do in the existing detail panel, read-only. Flags the detail state
   * (`isDeletedDetail = true`) for the "Deleted To-Do" header, then opens it via `openInDetail`. The To-Do is
   * forwarded with `archived: true` so the card's existing `todo.archived` read-only guards apply
   * (verified safe in 07-02: TodoCardStore fires no init-time API call gated on `archived`).
   */
  openDeletedTodoInDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodoTrashActions.openDeletedTodoInDetail),
      switchMap(({ todo }) => [
        TodoDetailActions.setIsDeletedDetail({ isDeletedDetail: true }),
        TodoDetailActions.openInDetail({ todo: { ...todo, archived: true } }),
      ])
    )
  );

  /**
   * DV-01 (Pitfall 2): Entering the trash view clears any open live-list detail's stale `selectedTodo` so a
   * previously-opened live-list To-Do detail cannot bleed into the trash view before a trash row is opened.
   * Separate single-responsibility effect — not folded into `clearSearchOnTrashToggle$`.
   */
  clearDetailOnTrashEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutsTodosEvents.showTrashItems),
      map(() => TodoDetailActions.clearSelectedTodo())
    )
  );

  /**
   * DV-02: Print the current trash collection. Mirrors `print$` but passes `showDeleted: true`.
   * NOTE: forward-compatible — the monolith todos print backend does not yet read `showDeleted`
   * (see 07-02-SUMMARY PRINT BACKEND ADDITION REQUIRED).
   */
  printDeletedTodos$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoTrashActions.printDeletedTodos),
        concatLatestFrom(() => [
          this.store.select(TeamSelectors.selectFilterBarTeamId),
          this.store.select(FilterUserSelectors.selectFilterBarUserIds),
          this.filterService.searchText$,
        ]),
        filter(([, teamId]) => !!teamId),
        switchMap(([{ printOptions }, teamId, filterBarUserIds, searchText]) => {
          const userIds =
            filterBarUserIds == null || filterBarUserIds.includes('all')
              ? []
              : filterBarUserIds.filter(userId => userId !== 'all');
          return this.printService.openPdf(PrintApi.todos, {
            teamId,
            printOptions,
            // The monolith print endpoint requires showArchived (non-empty boolean). Trash is its own
            // scope (deleted items), so it's always false here.
            showArchived: false,
            showDeleted: true,
            ...(userIds.length > 0 ? { userIds } : {}),
            ...(searchText?.trim() ? { searchText: searchText.trim() } : {}),
          });
        })
      ),
    { dispatch: false }
  );

  /**
   * DV-02: Routes the shared Excel-export trigger to the trash export ONLY when the trash view is active.
   * The live-list `downloadExcel$` is guarded off when `showTrash` is true (mutually exclusive).
   */
  routeTrashExcelExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.exportToExcel),
      filter(({ exportType }) => exportType === ExcelExportType.teamTodo),
      concatLatestFrom(() => [this.store.select(TeamTodoSelectors.selectShowTrash)]),
      filter(([, showTrash]) => !!showTrash),
      map(() => TeamTodoTrashActions.downloadDeletedTodosExcel())
    )
  );

  /**
   * DV-02: Downloads the trash collection as Excel. `{ dispatch: false }` ending in `catchError(() => EMPTY)`,
   * mirroring the rocks `downloadDeletedRocksExcel$` analog EXACTLY — dispatches NO success/failure action.
   */
  downloadDeletedTodosExcel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamTodoTrashActions.downloadDeletedTodosExcel),
        concatLatestFrom(() => [
          this.store.select(TeamSelectors.selectFilterBarTeamId),
          this.store.select(FilterUserSelectors.selectFilterBarUserIds),
          this.store.select(selectLanguage),
          this.store.select(TeamSelectors.selectFilterBarTeam),
          this.filterService.searchText$,
        ]),
        filter(([, teamId]) => !!teamId),
        switchMap(([, teamId, filterBarUserIds, language, team, searchText]) => {
          const userIds =
            filterBarUserIds == null || filterBarUserIds.includes('all')
              ? []
              : filterBarUserIds.filter(userId => userId !== 'all');

          return this.todoApi
            .downloadDeletedTodosExcel(teamId as string, userIds, searchText?.trim() || undefined)
            .pipe(
              tap(response => {
                const dateFormat = formatDate(new Date(), 'MMddyyyy', 'en-US');
                const fileName = `Deleted ${language?.todo?.items ?? 'To-Dos'} ${team?.name ?? ''} ${dateFormat}.xlsx`;
                this.fileService.downloadExcelFile(response, fileName);
              }),
              catchError(() => EMPTY)
            );
        })
      ),
    { dispatch: false }
  );
}
